import type { ScheduleItem } from '../../types/index.js'
import {
  addBroadcastDays, broadcastDayKey, instantAtBroadcastClock,
} from '../../lib/broadcast-day.js'

export type InfoserviceResizeEdge = 'START' | 'END'

const FROZEN_STATUSES = new Set(['LOADING', 'ON_AIR', 'DONE'])

export function infoserviceClockRange(date: string, startClock: string, endClock: string) {
  if (!date || !startClock || !endClock) return null
  const start = instantAtBroadcastClock(date, startClock)
  let end = instantAtBroadcastClock(date, endClock)
  // Une fin antérieure (ou égale) désigne le lendemain : l'infoservice peut
  // couvrir le passage de minuit sans imposer une seconde date à l'opérateur.
  if (end <= start) {
    end = instantAtBroadcastClock(broadcastDayKey(addBroadcastDays(date, 1)), endClock)
  }
  const durationSec = Math.round((end.getTime() - start.getTime()) / 1_000)
  return durationSec > 0 ? { start, end, durationSec } : null
}

function infoserviceSourceId(item: ScheduleItem): string | null {
  return item.adPlacements?.[0]?.spot?.id ?? null
}

export interface InfoserviceRun {
  memberIds: string[]
  firstId: string
  lastId: string
  startMs: number
  endMs: number
}

/**
 * Regroupe les passages successifs d'un même média d'infoservice.
 *
 * Le serveur découpe une plage longue en autant de lectures que nécessaire.
 * Sur la conduite, ces lectures forment pourtant un seul bandeau éditorial :
 * les poignées ne doivent donc apparaître qu'aux deux extrémités de la plage.
 */
export function infoserviceRuns(items: ScheduleItem[]): Map<string, InfoserviceRun> {
  const groups = new Map<string, ScheduleItem[]>()
  for (const item of items) {
    if (item.itemType !== 'INFOSERVICE' || FROZEN_STATUSES.has(item.status)) continue
    const sourceId = infoserviceSourceId(item)
    if (!sourceId) continue
    const key = `${item.gridId}:${sourceId}`
    const group = groups.get(key)
    if (group) group.push(item)
    else groups.set(key, [item])
  }

  const byItemId = new Map<string, InfoserviceRun>()
  const register = (members: ScheduleItem[]) => {
    if (members.length === 0) return
    const run: InfoserviceRun = {
      memberIds: members.map(item => item.id),
      firstId: members[0].id,
      lastId: members[members.length - 1].id,
      startMs: new Date(members[0].startTime).getTime(),
      endMs: new Date(members[members.length - 1].endTime).getTime(),
    }
    members.forEach(item => byItemId.set(item.id, run))
  }

  for (const candidates of groups.values()) {
    candidates.sort((left, right) => new Date(left.startTime).getTime() - new Date(right.startTime).getTime())
    let members: ScheduleItem[] = []
    for (const candidate of candidates) {
      const previous = members[members.length - 1]
      if (previous && new Date(previous.endTime).getTime() !== new Date(candidate.startTime).getTime()) {
        register(members)
        members = []
      }
      members.push(candidate)
    }
    register(members)
  }

  return byItemId
}

export function infoserviceRun(items: ScheduleItem[], itemId: string): InfoserviceRun | null {
  return infoserviceRuns(items).get(itemId) ?? null
}

/**
 * Plage d'infoservice qu'un ajout de dernière minute peut raccourcir pour se
 * faire une place.
 *
 * La coupe se fait par le bord tourné vers l'insertion, et jamais par l'autre :
 * le temps rendu doit être immédiatement repris par ce qu'on pose, sinon la
 * plage laisse un trou noir. Une plage qui *précède* l'intersection cède donc
 * sa fin, une plage qui la *suit* cède son début.
 */
export interface InfoserviceTrim {
  /** Passage servant de graine à l'appel de redimensionnement. */
  itemId: string
  memberIds: string[]
  /** Début actuel de la plage. */
  startTime: string
  /** Fin actuelle de la plage. */
  endTime: string
}

/** En deçà, la plage n'a plus rien à céder : le serveur refuserait la coupe. */
export const INFOSERVICE_TRIM_MIN_MS = 1_000

/**
 * Tolérance de *proximité*, utilisée pour retrouver l'élément ou la plage
 * d'infoservice qui borde une intersection. Un montage laisse régulièrement des
 * arrondis de quelques secondes : la remontée de chaîne les enjambe pour
 * désigner quand même le voisin visé par l'opérateur.
 *
 * Ce n'est **pas** le seuil qui décide s'il y a un trou : voir
 * `GLUED_TOLERANCE_MS`.
 */
export const ADJACENCY_TOLERANCE_MS = 10_000

/**
 * En deçà, deux éléments sont collés et le bord est une vraie intersection.
 * Au-delà, si court soit l'écart, c'est un trou volontaire — quatre secondes
 * laissées entre deux programmes pour y glisser un jingle en sont le cas
 * typique. L'y traiter comme une intersection ne proposerait que « Insérer »,
 * qui décale toute la séquence alors que la place existe déjà.
 */
export const GLUED_TOLERANCE_MS = 1_000

const SHIFT_BLOCKING_STATUSES = new Set(['LOADING', 'ON_AIR', 'DONE'])

/**
 * Une insertion entre deux programmes n'a pas de durée disponible par
 * définition. L'opérateur choisit donc le côté de la séquence qui cède du
 * temps : les programmes planifiés de ce côté se décalent jusqu'au premier
 * infoservice, qui se raccourcit d'autant.
 *
 * Un direct reste une ancre, tout comme une partie de bloc multipartie. Les
 * éléments déjà en cours ou passés sont filtrés par `isShiftable`.
 */
const SHIFTABLE_TYPES = new Set(['PROGRAM', 'JINGLE', 'AD_BREAK', 'INTERLUDE', 'FILLER'])

/**
 * Cherche la plage d'infoservice qui se termine sur cet instant.
 *
 * `toleranceMs` reprend le seuil d'adjacence de la grille : deux éléments
 * séparés de quelques secondes sont collés à l'œil de l'opérateur, et c'est
 * bien la plage d'avant qu'il désigne en demandant à insérer.
 */
export function infoserviceTrimEndingAt(
  runsByItem: Map<string, InfoserviceRun>,
  boundaryMs: number,
  toleranceMs = 10_000,
): InfoserviceTrim | null {
  for (const run of runsByItem.values()) {
    if (Math.abs(run.endMs - boundaryMs) > toleranceMs) continue
    if (run.endMs - run.startMs <= INFOSERVICE_TRIM_MIN_MS) return null
    return {
      itemId: run.lastId,
      memberIds: run.memberIds,
      startTime: new Date(run.startMs).toISOString(),
      endTime: new Date(run.endMs).toISOString(),
    }
  }
  return null
}

/**
 * Symétrique de la précédente : la plage qui *commence* sur cet instant.
 *
 * C'est le cas le plus courant en soirée, où l'habillage se lit
 * `[Programme][Jingle][Infoservice]` : la seule réserve de temps est en aval du
 * jingle. La plage retarde alors son début d'autant que dure l'insertion, et
 * l'insertion occupe la seconde même qu'elle vient de rendre.
 */
export function infoserviceTrimStartingAt(
  runsByItem: Map<string, InfoserviceRun>,
  boundaryMs: number,
  toleranceMs = 10_000,
): InfoserviceTrim | null {
  for (const run of runsByItem.values()) {
    if (Math.abs(run.startMs - boundaryMs) > toleranceMs) continue
    if (run.endMs - run.startMs <= INFOSERVICE_TRIM_MIN_MS) return null
    return {
      itemId: run.firstId,
      memberIds: run.memberIds,
      startTime: new Date(run.startMs).toISOString(),
      endTime: new Date(run.endMs).toISOString(),
    }
  }
  return null
}

/**
 * Signature d'un élément, indépendante de son horaire.
 *
 * Deux passages du même contenu doivent porter la même : c'est ce qui permet de
 * reconnaître, ailleurs dans la semaine, la copie d'une séquence qu'on vient de
 * modifier. Rien ne relie une rediffusion à sa source en base — seule la
 * composition de la séquence en témoigne.
 */
export function itemIdentityKey(item: ScheduleItem): string {
  if (item.programId) return `P:${item.programId}`
  if (item.interludeClipId) return `C:${item.interludeClipId}`
  if (item.itemType === 'INFOSERVICE') return `I:${infoserviceSourceId(item) ?? '?'}`
  if (item.itemType === 'AD_BREAK') {
    return `A:${(item.adPlacements ?? []).map(placement => placement.spot?.id ?? '?').join('+')}`
  }
  return `${item.itemType}:${item.durationSec}`
}

/** Élément de la chaîne à décaler d'autant que l'infoservice recule. */
export interface InsertionShift {
  id: string
  startMs: number
  itemType: ScheduleItem['itemType']
}

/**
 * Recette complète d'une insertion « en force » à une intersection.
 *
 * L'infoservice ne borde pas toujours le point visé : entre lui et
 * l'intersection s'intercalent souvent un jingle, un coming next, un écran
 * publicitaire. Le plan décrit alors les deux gestes indissociables — la plage
 * rend du temps par le bord qui regarde l'intersection, et l'habillage qui les
 * sépare se décale d'autant, dans le sens qui referme le trou.
 */
export interface InfoserviceInsertPlan {
  /** Instant visé : la fin de l'élément qui précède, le début de celui qui suit. */
  boundaryMs: number
  /**
   * Bord de la plage qui cède du temps, et donc sens du geste. `END` : la plage
   * est en amont, sa fin et la chaîne se déplacent plus tôt ; l'ajout se
   * termine sur l'intersection. `START` : la plage est en aval, son début et la
   * chaîne se déplacent plus tard ; l'ajout commence sur l'intersection.
   */
  edge: InfoserviceResizeEdge
  trim: InfoserviceTrim
  /** Éléments collés à décaler, dans l'ordre chronologique. */
  shift: InsertionShift[]
  /** Durée maximale que la plage peut céder. */
  slackMs: number
  /** Voisins immédiats de l'intersection, pour retrouver ses copies. */
  signature: { before: string | null; after: string | null }
}

const mapKey = (item: ScheduleItem | null) => item ? itemIdentityKey(item) : null

function itemEndingAt(items: ScheduleItem[], boundaryMs: number, toleranceMs: number): ScheduleItem | null {
  let best: ScheduleItem | null = null
  for (const item of items) {
    const endMs = new Date(item.endTime).getTime()
    // Un élément qui finit après le curseur se trouve de l'autre côté de la
    // frontière. La tolérance ne doit jamais le faire repasser en amont.
    if (endMs > boundaryMs) continue
    if (Math.abs(endMs - boundaryMs) > toleranceMs) continue
    if (!best || endMs > new Date(best.endTime).getTime()) best = item
  }
  return best
}

function itemStartingAt(items: ScheduleItem[], boundaryMs: number, toleranceMs: number): ScheduleItem | null {
  let best: ScheduleItem | null = null
  for (const item of items) {
    const startMs = new Date(item.startTime).getTime()
    // Symétriquement, un élément déjà commencé ne peut pas redevenir le
    // prochain voisin simplement parce qu'il dure moins que la tolérance.
    if (startMs < boundaryMs) continue
    if (Math.abs(startMs - boundaryMs) > toleranceMs) continue
    if (!best || startMs < new Date(best.startTime).getTime()) best = item
  }
  return best
}

/** Un élément planifié se laisse décaler ; les ancres arrêtent la chaîne. */
function isShiftable(item: ScheduleItem | null): item is ScheduleItem {
  if (!item || !SHIFTABLE_TYPES.has(item.itemType)) return false
  return !SHIFT_BLOCKING_STATUSES.has(item.status) && !item.bundleId
}

/**
 * Construit les plans d'insertion à une intersection, en cherchant de part et
 * d'autre la première plage d'infoservice atteignable.
 *
 * Les deux sens sont indispensables. Une soirée se monte aussi bien en
 * `[Infoservice][Jingle][Programme]` qu'en `[Programme][Jingle][Infoservice]` :
 * ne chercher qu'en amont, c'est refuser toute insertion autour du jingle dans
 * le second cas, alors que la réserve de temps est là, juste en dessous. À
 * Quand les deux côtés sont possibles, les deux plans sont rendus : la fenêtre
 * d'ajout laisse l'opérateur choisir l'infoservice du haut ou celui du bas.
 *
 * La recherche s'arrête sur un direct, un élément figé ou une partie d'un bloc
 * multipartie. Ces ancres ne doivent jamais bouger silencieusement.
 */
export function infoserviceInsertPlansAt(
  items: ScheduleItem[],
  runsByItem: Map<string, InfoserviceRun>,
  boundaryMs: number,
  toleranceMs = 10_000,
): InfoserviceInsertPlan[] {
  const live = items.filter(item => item.status !== 'SKIPPED')
  const signature = {
    before: mapKey(itemEndingAt(live, boundaryMs, toleranceMs)),
    after: mapKey(itemStartingAt(live, boundaryMs, toleranceMs)),
  }

  const finish = (edge: InfoserviceResizeEdge, trim: InfoserviceTrim, shift: InsertionShift[]) => {
    const runStartMs = new Date(trim.startTime).getTime()
    const runEndMs = new Date(trim.endTime).getTime()
    return {
      boundaryMs,
      edge,
      trim,
      shift,
      slackMs: Math.max(0, runEndMs - runStartMs - INFOSERVICE_TRIM_MIN_MS),
      signature,
    }
  }

  const plans: InfoserviceInsertPlan[] = []

  // Amont : la plage rend sa fin, la chaîne se déplace plus tôt vers elle.
  const upstream: InsertionShift[] = []
  let cursor = boundaryMs
  for (let depth = 0; depth <= live.length; depth++) {
    const previous = itemEndingAt(live, cursor, toleranceMs)
    const trim = infoserviceTrimEndingAt(runsByItem, cursor, toleranceMs)
    // Une tolérance de dix secondes absorbe les petits arrondis, mais un
    // jingle de quatre secondes reste un vrai élément. La plage n'est le
    // voisin immédiat que si l'item le plus proche appartient bien à sa run.
    if (trim && (!previous || trim.memberIds.includes(previous.id))) {
      plans.push(finish('END', trim, upstream))
      break
    }

    // Une plage d'infoservice qui n'a pas donné de `trim` est gelée ou orpheline
    // de sa source : on ne la décale pas comme un jingle.
    if (!isShiftable(previous)) break

    upstream.unshift({
      id: previous.id,
      startMs: new Date(previous.startTime).getTime(),
      itemType: previous.itemType,
    })
    cursor = new Date(previous.startTime).getTime()
  }

  // Aval : la plage retarde son début, la chaîne se déplace plus tard vers elle.
  const downstream: InsertionShift[] = []
  cursor = boundaryMs
  for (let depth = 0; depth <= live.length; depth++) {
    const next = itemStartingAt(live, cursor, toleranceMs)
    const trim = infoserviceTrimStartingAt(runsByItem, cursor, toleranceMs)
    if (trim && (!next || trim.memberIds.includes(next.id))) {
      plans.push(finish('START', trim, downstream))
      break
    }

    if (!isShiftable(next)) break

    downstream.push({
      id: next.id,
      startMs: new Date(next.startTime).getTime(),
      itemType: next.itemType,
    })
    cursor = new Date(next.endTime).getTime()
  }

  return plans
}

/**
 * Compatibilité avec les appels qui n'ont pas de choix à présenter : l'amont
 * reste prioritaire, comme avant l'ajout de la sélection haut/bas.
 */
export function infoserviceInsertPlanAt(
  items: ScheduleItem[],
  runsByItem: Map<string, InfoserviceRun>,
  boundaryMs: number,
  toleranceMs = 10_000,
): InfoserviceInsertPlan | null {
  return infoserviceInsertPlansAt(items, runsByItem, boundaryMs, toleranceMs)[0] ?? null
}

interface ResizeBoundaryInput {
  run: InfoserviceRun
  edge: InfoserviceResizeEdge
  deltaMs: number
  neighbours: Array<{ startMs: number; endMs: number }>
  dayStartMs: number
  dayEndMs: number
  snapMinutes: number
  magnetMs: number
  minDurationMs?: number
}

/**
 * Convertit le geste vertical en nouvelle plage, sans jamais traverser un
 * voisin ni sortir de la journée d'antenne affichée.
 */
export function resolveInfoserviceResize(input: ResizeBoundaryInput): { startMs: number; endMs: number } {
  const minDurationMs = input.minDurationMs ?? 1_000
  const stepMs = Math.max(1_000, Math.round(input.snapMinutes * 60_000))
  const rawBoundary = (input.edge === 'START' ? input.run.startMs : input.run.endMs) + input.deltaMs
  const snappedToGrid = input.dayStartMs + Math.round((rawBoundary - input.dayStartMs) / stepMs) * stepMs

  if (input.edge === 'START') {
    const previousEnd = input.neighbours
      .filter(item => item.endMs <= input.run.startMs)
      .reduce<number | null>((nearest, item) => nearest === null || item.endMs > nearest ? item.endMs : nearest, null)
    const boundary = previousEnd !== null && Math.abs(rawBoundary - previousEnd) <= input.magnetMs
      ? previousEnd
      : snappedToGrid
    const lower = Math.max(input.dayStartMs, previousEnd ?? input.dayStartMs)
    return {
      startMs: Math.min(input.run.endMs - minDurationMs, Math.max(lower, boundary)),
      endMs: input.run.endMs,
    }
  }

  const nextStart = input.neighbours
    .filter(item => item.startMs >= input.run.endMs)
    .reduce<number | null>((nearest, item) => nearest === null || item.startMs < nearest ? item.startMs : nearest, null)
  const boundary = nextStart !== null && Math.abs(rawBoundary - nextStart) <= input.magnetMs
    ? nextStart
    : snappedToGrid
  const upper = Math.min(input.dayEndMs, nextStart ?? input.dayEndMs)
  return {
    startMs: input.run.startMs,
    endMs: Math.max(input.run.startMs + minDurationMs, Math.min(upper, boundary)),
  }
}
